import { VerificationMethod } from '@sparelabs/api-client'
import React, { SyntheticEvent, useEffect, useState } from 'react'
import { useAsync } from 'react-use'
import { ApiClientBuilder, EngageClientBuilder } from 'src/api/ApiClientBuilders'
import { LoadingView } from 'src/components'
import { ErrorHandler, FormValidator, ValidateConstraintType, withErrorHandler } from 'src/helpers'
import { AuthenticationHelper } from 'src/helpers/AuthenticationHelper'
import { st } from 'src/locales/TranslationHelper'
import { RiderAuthEmailForm } from 'src/pages/Form/steps/RiderAuth/RiderAuthEmailForm'
import { RiderAuthPhoneForm } from 'src/pages/Form/steps/RiderAuth/RiderAuthPhoneForm'
import { RiderAuthVerifyEmailForm } from 'src/pages/Form/steps/RiderAuth/RiderAuthVerifyEmailForm'
import { RiderAuthVerifyPhoneForm } from 'src/pages/Form/steps/RiderAuth/RiderAuthVerifyPhoneForm'
import { IFormContext, useFormContext } from '../FormContext'
import { FormSubmitted } from '../FormStepTypes'

const validatePhoneNumber = (value?: string) =>
  FormValidator.validate(value, st.forms.auth.phoneNumberLabel(), {
    required: true,
    type: ValidateConstraintType.PhoneNumber,
  })

const validateEmail = (value?: string) =>
  FormValidator.validate(value, st.forms.auth.emailLabel(), {
    required: true,
    type: ValidateConstraintType.Email,
  })

const validateCode = (value?: string) => FormValidator.validate(value, st.forms.auth.codeLabel(), { required: true })

/**
 * Grab previous case form if it exists
 */
const postAuthUpdate = async (
  setCaseForm: IFormContext['setCaseForm'],
  formId: string,
  caseId?: string,
  abortSignal?: AbortSignal,
) => {
  if (!caseId) {
    return
  }

  const client = EngageClientBuilder.build()

  // Validate caseId
  const userCase = await client.cases.get(caseId)
  if (!userCase) {
    throw new Error(st.errors.noPermission())
  }

  // Fetch previous case form
  const caseForm = (await client.caseForms.list({ caseId, formId })).data[0]
  if (caseForm && !abortSignal?.aborted) {
    setCaseForm(caseForm, caseForm ? FormSubmitted.Previously : FormSubmitted.Never)
  }
}

export const RiderAuthStep = (): JSX.Element => {
  const { caseId, form, organization, onNextStep, setCaseForm, showEmailLoginFirst } = useFormContext()
  const [showVerify, setShowVerify] = useState(false)
  // Credential is either a phone number or an email.
  const [credential, setCredential] = useState<string | undefined>(undefined)
  const [isUsingEmail, setIsUsingEmail] = useState<boolean>(showEmailLoginFirst ?? false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(true)

  const { value } = useAsync(async () => {
    const client = ApiClientBuilder.build()
    return client.public.getTerms({ organizationId: organization.id })
  })

  const onBackClick = () => {
    setShowVerify(false)
    window.scrollTo(0, 0)
  }

  useEffect(() => {
    const controller = new AbortController()
    async function checkAuth() {
      if (AuthenticationHelper.isAuthenticated()) {
        if (form) {
          await postAuthUpdate(setCaseForm, form.id, caseId, controller.signal)
        }
        onNextStep()
      } else {
        setLoading(false)
      }
    }

    checkAuth().catch(ErrorHandler.handleError)
    return () => controller.abort()
  }, [form, caseId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLinkClick = (event: SyntheticEvent) => {
    setError(undefined)
    setCredential('')
    event.preventDefault()
    setIsUsingEmail((prevState) => !prevState)
  }

  const sendLoginRequest = async (credential: string | undefined, method?: VerificationMethod) => {
    if (!credential) {
      setShowVerify(false)
      setError(isUsingEmail ? st.validation.invalidEmail() : st.validation.invalidPhoneNumber())
      return
    }

    if (isUsingEmail) {
      const client = ApiClientBuilder.build()
      await client.auth.emailLoginRequest({ email: credential, organizationId: organization.id })
    } else {
      const client = ApiClientBuilder.build()

      await client.auth.phoneLoginRequest({
        phoneNumber: credential,
        verificationMethod: method ?? VerificationMethod.Sms,
        organizationId: organization.id,
      })
    }
  }

  const handleCredentialSubmit = withErrorHandler(async (credential: string) => {
    if (isUsingEmail) {
      const credentialError = validateEmail(credential)
      if (credentialError) {
        setError(credentialError)
        return
      }
    } else {
      const credentialError = validatePhoneNumber(credential)
      if (credentialError) {
        setError(credentialError)
        return
      }
    }

    setError(undefined)
    await sendLoginRequest(credential)
    setShowVerify(true)
    setCredential(credential)
  })

  const handleVerifySubmit = withErrorHandler(async (code: string) => {
    if (!credential) {
      setShowVerify(false)
      setError(st.validation.invalidFields())
      return
    }

    const codeError = validateCode(code)
    if (codeError) {
      setError(codeError)
      return
    }

    setError(undefined)
    if (isUsingEmail) {
      const client = ApiClientBuilder.build()
      const result = await client.auth.emailLoginVerify({
        email: credential,
        code,
        organizationId: organization.id,
      })
      AuthenticationHelper.setAuth(result, true)
    } else {
      const client = ApiClientBuilder.build()
      const result = await client.auth.phoneLoginVerify({
        phoneNumber: credential,
        code,
        organizationId: organization.id,
      })

      AuthenticationHelper.setAuth(result, false)
    }

    if (form) {
      await postAuthUpdate(setCaseForm, form.id, caseId)
    }
    onNextStep()
  })

  if (loading) {
    return <LoadingView />
  }

  const handleResendClick = withErrorHandler((method: VerificationMethod) => sendLoginRequest(credential, method))

  const CurrentForm = () => {
    if (showVerify) {
      return isUsingEmail ? (
        <RiderAuthVerifyEmailForm
          onSubmit={handleVerifySubmit}
          onResendClick={handleResendClick}
          onBackClick={onBackClick}
          error={error}
        />
      ) : (
        <RiderAuthVerifyPhoneForm
          onSubmit={handleVerifySubmit}
          onResendClick={handleResendClick}
          onBackClick={onBackClick}
          error={error}
        />
      )
    }

    return isUsingEmail ? (
      <RiderAuthEmailForm
        error={error}
        onSubmit={handleCredentialSubmit}
        terms={value}
        handleLinkClick={handleLinkClick}
      />
    ) : (
      <RiderAuthPhoneForm
        error={error}
        onSubmit={handleCredentialSubmit}
        terms={value}
        handleLinkClick={handleLinkClick}
      />
    )
  }

  return <CurrentForm />
}
